export default function initSupportWizard() {
    const container = document.querySelector("#supportWizard");
    if (!container) return;

    const isWelsh = document.documentElement.lang.toLowerCase().startsWith("cy");

    const CONFIG = {
        animationDuration: 300,
        screens: ["intro", "0", "q1", "q2", "q3", "q4", "q5", "result", "form"],
        questionOrder: ["0", "q1", "q2", "q3", "q4", "q5"]
    };

    const DEFAULT_RECOMMENDATION = {
        hasAlert: true,
        hasActive: false,
        hasSupport: false,
        hasResponse: false
    };

    // -------------------------------------------------------
    // BILINGUAL RECOMMENDATION MESSAGES
    // -------------------------------------------------------
    const RECOMMENDATION_MESSAGES = {
        alert: {
            textEn: "Based on your answers, we recommend the basic <strong>Alert package</strong> to give you peace of mind that if something was to happen, someone is available 24/7 to get you the help you need.",
            textCy: "Yn seiliedig ar eich atebion, rydym yn argymell y pecyn <strong>Delta yn y Cartref</strong> sylfaenol i roi tawelwch meddwl i chi, pe bai rhywbeth yn digwydd, fod rhywun ar gael 24/7 i roi'r cymorth sydd ei angen arnoch chi."
        },
        "alert-support": {
            textEn: "Based on your answers, we recommend the <strong>Alert package</strong>, with the <strong>Support</strong> add-on for regular wellbeing calls.",
            textCy: "Yn seiliedig ar eich atebion, rydym yn argymell y <strong>pecyn Delta yn y Cartref</strong>, gyda <strong>Delta Sgwrsio fel ychwanegiad</strong> i gael galwadau llesiant rheolaidd."
        },
        "alert-respond": {
            textEn: "We recommend the <strong>Alert + Respond</strong> package, as you don't always have someone nearby in an emergency.",
            textCy: "Rydym yn argymell y pecyn <strong>Delta yn y Cartref a Delta ar ei Ffordd</strong>, gan nad oes gennych rywun gerllaw bob amser mewn argyfwng."
        },
        "alert-support-respond": {
            textEn: "You'd benefit most from the full package: <strong>Alert + Support + Respond</strong> – giving you reassurance, regular wellbeing calls, and 24/7 home response.",
            textCy: "Byddech chi'n elwa fwyaf o'r pecyn llawn: <strong>Delta yn y Cartref, Delta Sgwrsio a Delta ar ei Ffordd</strong> - gan roi sicrwydd i chi, galwadau llesiant rheolaidd, ac ymateb yn y cartref 24/7."
        },
        active: {
            textEn: "As you spend time out and about, we recommend our <strong>Active</strong> offer so that you can get help wherever you are, both at home and on the go.",
            textCy: "Gan eich bod yn treulio amser allan ar hyd y lle, rydym yn argymell ein cynnig <strong>Delta Unrhyw Le</strong> fel y gallwch gael help lle bynnag yr ydych chi, gartref ac wrth fynd o le i le."
        },
        "active-support": {
            textEn: "We recommend our <strong>Active + Support</strong> – keeping you protected on the go with regular wellbeing calls to check in on you.",
            textCy: "Rydym yn argymell ein cynnig <strong>Delta Unrhyw Le + Delta Sgwrsio</strong> – yn eich cadw'n ddiogel wrth fynd gyda galwadau llesiant rheolaidd i holi sut rydych chi."
        },
        "active-respond": {
            textEn: "You'd benefit most from our <strong>Active + Respond</strong> – giving you reassurance and 24/7 local response.",
            textCy: "Byddech chi'n elwa fwyaf ar ein cynnig <strong>Delta Unrhyw Le a Delta ar ei Ffordd</strong>- gan roi sicrwydd i chi a gwasanaeth ymateb lleol 24/7."
        },
        "active-support-respond": {
            textEn: "We recommend our <strong>Active + Support + Respond</strong> – giving you reassurance both at home and when out and about, as well as regular wellbeing calls, and 24/7 response.",
            textCy: "Rydym yn argymell ein cynnig <strong>Delta Unrhyw Le, Delta Sgwrsio a Delta ar ei Ffordd</strong> – gan roi sicrwydd i chi gartref a phan fyddwch chi allan, yn ogystal â galwadau llesiant rheolaidd, ac ymateb 24/7."
        }
    };

    let state = {
        currentScreen: "intro",
        answers: {},
        recommendation: { ...DEFAULT_RECOMMENDATION }
    };

    const screens = getScreens();
    bindEvents();
    showScreen("intro");

    // -------------------------------------------------------
    // SCREEN SETUP
    // -------------------------------------------------------
    function getScreens() {
        const map = {};
        CONFIG.screens.forEach(id => {
            map[id] = container.querySelector(`[data-screen="${id}"]`);
        });
        return map;
    }

    function bindEvents() {
        container.addEventListener("click", handleContainerClick);

        const startBtn = container.querySelector('[data-action="start"]');
        startBtn?.addEventListener("click", () => transitionToScreen("0"));

        const restartBtn = container.querySelector('[data-action="restart"]');
        restartBtn?.addEventListener("click", handleRestart);
    }

    function handleContainerClick(e) {
        const answerCircle = e.target.closest(".answer-circle");
        if (answerCircle) {
            selectAnswer(answerCircle.dataset.answer);
            return;
        }

        if (e.target.closest('[data-action="back"]')) {
            handleBack();
            return;
        }

        if (e.target.closest('[data-action="next"]')) {
            handleNext();
            return;
        }

        if (e.target.closest('[data-action="showForm"]')) {
            handleShowForm();
        }
    }

    // -------------------------------------------------------
    // NAVIGATION
    // -------------------------------------------------------
    function handleBack() {
        const idx = CONFIG.questionOrder.indexOf(state.currentScreen);
        if (idx <= 0) {
            transitionToScreen("intro", true);
        } else {
            transitionToScreen(CONFIG.questionOrder[idx - 1], true);
        }
    }

    function handleNext() {
        const current = state.currentScreen;
        const answer = state.answers[current];
        if (answer === undefined) return;

        updateRecommendation(current, answer);

        const idx = CONFIG.questionOrder.indexOf(current);
        if (idx < CONFIG.questionOrder.length - 1) {
            transitionToScreen(CONFIG.questionOrder[idx + 1]);
        } else {
            updateResultScreen();
            transitionToScreen("result");
        }
    }

    function handleRestart() {
        state = {
            currentScreen: "intro",
            answers: {},
            recommendation: { ...DEFAULT_RECOMMENDATION }
        };

        container.querySelectorAll(".circle-btn").forEach(btn =>
            btn.classList.remove("selected-yes", "selected-no")
        );

        showScreen("intro");
    }

    function handleShowForm() {
        transitionToScreen("form");
    }

    // -------------------------------------------------------
    // ANSWERS & RECOMMENDATION LOGIC
    // -------------------------------------------------------
    function selectAnswer(answerValue) {
        const screenEl = screens[state.currentScreen];
        const circles = screenEl.querySelectorAll(".answer-circle");
        const selectedAnswer = state.currentScreen === "0"
            ? answerValue
            : answerValue === "yes";

        circles.forEach(circle => {
            const btn = circle.querySelector(".circle-btn");
            btn.classList.remove("selected-yes", "selected-no");

            if (circle.dataset.answer === answerValue) {
                btn.classList.add(answerValue === "no" ? "selected-no" : "selected-yes");
            }
        });

        state.answers[state.currentScreen] = selectedAnswer;
        updateNextButtonState();
    }

    function updateNextButtonState() {
        const nextBtn = screens[state.currentScreen].querySelector('[data-action="next"]');
        if (!nextBtn) return; 
        const hasAnswer = state.answers[state.currentScreen] !== undefined;
        nextBtn.disabled = !hasAnswer;
        nextBtn.classList.toggle("disabled", !hasAnswer);
    }

    function updateRecommendation(questionId, answer) {
        switch (questionId) {
            case "0":
                break;
            case "q1":
                if (!answer) state.recommendation.hasResponse = true;
                break;
            case "q2":
            case "q3":
                if (answer) state.recommendation.hasSupport = true;
                break;
            case "q4":
                if (answer) state.recommendation.hasActive = true;
                break;
            case "q5":
                if (answer) state.recommendation.hasResponse = true;
                break;
        }
    }

    // -------------------------------------------------------
    // RESULT SCREEN
    // -------------------------------------------------------
    function updateResultScreen() {
        const resultScreen = screens.result;
        const messageKey = getMessageKey(state.recommendation);
        const message = RECOMMENDATION_MESSAGES[messageKey];

        const textEl = resultScreen.querySelector(".result-text");
        textEl.innerHTML = isWelsh ? message.textCy : message.textEn;

        const ctaBtn = resultScreen.querySelector(".btn-cta");
        const ctaData = resultScreen.querySelector(`[data-package="${messageKey}"]`);

        if (ctaData) {
            ctaBtn.href = ctaData.dataset.url || "#";
            ctaBtn.textContent = isWelsh
                ? ctaData.dataset.labelCy
                : ctaData.dataset.labelEn;
        }
    }

    function getMessageKey(rec) {
        const parts = [rec.hasActive ? "active" : "alert"];
        if (rec.hasSupport) parts.push("support");
        if (rec.hasResponse) parts.push("respond");
        return parts.join("-");
    }

    // -------------------------------------------------------
    // SCREEN TRANSITIONS
    // -------------------------------------------------------
    function transitionToScreen(screenId, isBack = false) {
        const currentEl = screens[state.currentScreen];
        const nextEl = screens[screenId];

        currentEl.classList.add(isBack ? "animate-fade-out" : "animate-slide-out");

        setTimeout(() => {
            currentEl.classList.remove("active", "animate-fade-out", "animate-slide-out");

            nextEl.classList.add("active");
            nextEl.classList.add(screenId === "result" ? "animate-scale-bounce" : "animate-slide-in");

            setTimeout(() => {
                nextEl.classList.remove("animate-slide-in", "animate-scale-bounce");
            }, CONFIG.animationDuration + 200);

            state.currentScreen = screenId;
            updateNextButtonState();
        }, CONFIG.animationDuration);
    }

    function showScreen(screenId) {
        Object.values(screens).forEach(s => s.classList.remove("active"));
        screens[screenId].classList.add("active");
        state.currentScreen = screenId;
        updateNextButtonState();
    }

    return {
        restart: handleRestart,
        getRecommendation: () => ({ ...state.recommendation }),
        getAnswers: () => ({ ...state.answers })
    };
}